import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import PageHeader from "../components/PageHeader";
import { UserCog, FileText, Users, Settings2, AlertTriangle, Euro, Wallet, Building2 } from "lucide-react";
import { PayslipsTab } from "../components/payroll/PayslipsTab";
import { EmployeesTab } from "../components/payroll/EmployeesTab";
import { PayrollSettingsTab } from "../components/payroll/PayrollSettingsTab";

const TABS = [
  { key: "payslips", label: "Bulletins", icon: FileText },
  { key: "employees", label: "Salariés", icon: Users },
  { key: "settings", label: "Paramètres & barème", icon: Settings2 },
];

const eur = (v) => (v == null ? "—" : Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));

export default function SocialPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const { currentSociete, societes, switchSociete } = useSociete();
  const societeId = currentSociete?.societe_id || societes?.[0]?.societe_id;
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(TABS.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "payslips");
  const [stats, setStats] = useState(null);

  const loadStats = () => {
    if (!societeId) return;
    api.get("/payroll/stats", { params: { societe_id: societeId } }).then((r) => setStats(r.data)).catch(() => setStats(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadStats, [societeId, tab]);

  if (!societeId) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-xl p-4 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Aucune société sélectionnée.
        </div>
      </div>
    );
  }

  const kpis = [
    { label: "Salariés actifs", value: stats?.employees_active ?? "—", icon: Users, color: "#8b5cf6", testid: "kpi-employees" },
    { label: stats?.last_period_label ? `Net payé · ${stats.last_period_label}` : "Net payé (dernière paie)", value: eur(stats?.last_net), icon: Wallet, color: "#22c55e", testid: "kpi-last-net" },
    { label: "Coût employeur (dernière paie)", value: eur(stats?.last_cost), icon: Euro, color: "#f7c935", testid: "kpi-last-cost" },
    { label: "Masse salariale brute (année)", value: eur(stats?.gross_ytd), icon: Building2, color: "#38bdf8", testid: "kpi-gross-ytd" },
  ];

  return (
    <div className="p-6 max-w-[1280px] mx-auto" data-testid="social-page">
      <PageHeader
        icon={<UserCog />}
        title="Social — Paie"
        description="Dossiers salariés · Bulletins de paie · Écritures automatiques · Espace salarié"
        testId="social-header"
        actions={societes && societes.length > 1 ? (
          <select value={societeId} onChange={(e) => { const s = societes.find((x) => x.societe_id === e.target.value); if (s) switchSociete(s); }}
            className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:border-[#f7c935] outline-none" data-testid="payroll-societe-selector">
            {societes.map((s) => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
          </select>
        ) : <div className="text-xs text-slate-500">Société : <span className="text-white font-bold">{currentSociete?.name}</span></div>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {kpis.map((c) => (
          <div key={c.label} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={c.testid}>
            <div className="flex items-center gap-3">
              <c.icon className="w-5 h-5 shrink-0" style={{ color: c.color }} />
              <div className="min-w-0">
                <div className="text-lg font-bold truncate" style={{ color: c.color }}>{c.value}</div>
                <div className="text-[11px] text-slate-500 truncate">{c.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {stats?.last_due && stats.last_period && (
        <div className="flex flex-wrap gap-2 mb-5 text-[11px]" data-testid="payroll-due-strip">
          <span className="text-slate-500 font-bold uppercase tracking-wider">À régler · {stats.last_period_label} :</span>
          {[["URSSAF", stats.last_due.urssaf], ["Retraite", stats.last_due.retraite], ["Prévoyance", stats.last_due.prevoyance], ["Mutuelle", stats.last_due.mutuelle], ["PAS (DGFiP)", stats.last_due.pas]]
            .filter(([, v]) => v > 0).map(([k, v]) => <span key={k} className="px-2 py-0.5 rounded bg-white/5 text-slate-300">{k} <b className="text-white">{eur(v)}</b></span>)}
        </div>
      )}

      <div className="flex flex-wrap gap-1 mb-5 bg-[#0b0811] border border-white/5 rounded-xl p-1" data-testid="payroll-tabs">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`payroll-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${active ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
              {t.key === "payslips" && stats?.drafts > 0 && <span className={`ml-1 text-[10px] font-black px-1.5 py-0.5 rounded-full ${active ? "bg-[#0b0811] text-[#f7c935]" : "bg-[#f97316] text-[#0b0811]"}`}>{stats.drafts}</span>}
            </button>
          );
        })}
      </div>

      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
        {tab === "payslips" && <PayslipsTab api={api} societeId={societeId} onChanged={loadStats} />}
        {tab === "employees" && <EmployeesTab api={api} societeId={societeId} onChanged={loadStats} />}
        {tab === "settings" && <PayrollSettingsTab api={api} societeId={societeId} />}
      </div>
    </div>
  );
}
